import { useState, useCallback, useEffect, useRef } from "react";
import QuoteCarousel from "@/components/QuoteCarousel";
import { LogoMark } from "@/components/Logo";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { UserButton } from "@clerk/react";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, Download, RefreshCw, ChevronLeft, ChevronRight,
  TrendingUp, DollarSign, Users, AlertTriangle, Shield, BarChart3,
  CheckCircle, Clock, Cpu, RotateCcw, XCircle,
} from "lucide-react";

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmt(n: number | string | null | undefined, decimals = 2): string {
  if (n == null) return "$0.00";
  const v = typeof n === "string" ? parseFloat(n) : n;
  return "$" + v.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function fmtNum(n: number | string | null | undefined): string {
  if (n == null) return "0";
  const v = typeof n === "string" ? parseFloat(n) : n;
  return v.toLocaleString("en-US");
}

function ago(ts: Date | null | undefined): string {
  if (!ts) return "—";
  const secs = Math.floor((Date.now() - ts.getTime()) / 1000);
  if (secs < 5) return "just now";
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  return `${Math.floor(secs / 3600)}h ago`;
}

function shortDate(d: string | Date | null | undefined): string {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function exportCsv(filename: string, headers: string[], rows: (string | number | null | undefined)[][]) {
  const escape = (v: string | number | null | undefined) =>
    `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [headers.map(escape).join(","), ...rows.map((r) => r.map(escape).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const SHEET_LABELS = ["Financial", "Donations", "Debt Queue", "Payouts", "Users", "Pools", "Fraud", "Jobs"];
const CHART_COLORS = ["#6366f1", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4"];

// ── LastUpdated ────────────────────────────────────────────────────────────────
// Uses a 5-second interval to continuously rerender the "X seconds ago" text.

function LastUpdated({ dataUpdatedAt, isFetching }: { dataUpdatedAt: number; isFetching: boolean }) {
  const [, setTick] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    intervalRef.current = setInterval(() => setTick((t) => t + 1), 5000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  const ts = dataUpdatedAt ? new Date(dataUpdatedAt) : null;
  return (
    <span className="text-xs text-muted-foreground flex items-center gap-1">
      {isFetching ? (
        <RefreshCw className="h-3 w-3 animate-spin" />
      ) : (
        <RefreshCw className="h-3 w-3" />
      )}
      Last updated: {ts ? ago(ts) : "—"} · auto-refreshes every 60s
    </span>
  );
}

// ── Pagination ─────────────────────────────────────────────────────────────────

function Pagination({ page, pages, onChange }: { page: number; pages: number; onChange: (p: number) => void }) {
  if (pages <= 1) return null;
  return (
    <div className="flex items-center justify-center gap-2 pt-4">
      <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => onChange(page - 1)}>
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <span className="text-sm text-muted-foreground">Page {page} of {pages}</span>
      <Button variant="ghost" size="sm" disabled={page >= pages} onClick={() => onChange(page + 1)}>
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
}

// ── Sheet 1: Financial Overview ────────────────────────────────────────────────

interface FinancialData {
  pools: Array<{
    id: number; name: string; status: string;
    balance: string; threshold: string; percentFunded: number;
    donatedThisMonth: string; totalPaidOut: string;
  }>;
  platform: {
    totalDonations: string; totalDonationCount: number;
    totalPaidOut: string; netHeld: string;
    donatedThisMonth: string; paidOutThisMonth: string;
  };
  cashFlow: Array<{ week: string; inflow: number; outflow: number }>;
  monthlyTrend: Array<{ month: string; inflow: number; outflow: number }>;
}

function FinancialSheet() {
  const { data, isFetching, dataUpdatedAt } = useQuery<FinancialData>({
    queryKey: ["analytics-financial"],
    queryFn: () => fetch("/api/admin/analytics/financial", { credentials: "include" }).then((r) => r.json()),
    refetchInterval: 60000,
  });

  const handleExport = useCallback(() => {
    if (!data) return;
    exportCsv("financial-pools.csv",
      ["Pool", "Status", "Balance", "Threshold", "% Funded", "Donated This Month"],
      (data.pools ?? []).map((p) => [p.name, p.status, p.balance, p.threshold,
        p.percentFunded.toFixed(1) + "%", p.donatedThisMonth]),
    );
  }, [data]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <LastUpdated dataUpdatedAt={dataUpdatedAt ?? 0} isFetching={isFetching} />
        <Button variant="outline" size="sm" onClick={handleExport} disabled={!data}>
          <Download className="h-4 w-4 mr-1" /> Export CSV
        </Button>
      </div>

      {/* Platform Totals */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {[
            { label: "Total Donated", value: fmt(data.platform.totalDonations), sub: `${fmtNum(data.platform.totalDonationCount)} donations` },
            { label: "Total Paid Out", value: fmt(data.platform.totalPaidOut), sub: "all time" },
            { label: "Net Held (Escrow)", value: fmt(data.platform.netHeld), sub: "available" },
            { label: "Donated This Month", value: fmt(data.platform.donatedThisMonth), sub: "current month" },
            { label: "Paid Out This Month", value: fmt(data.platform.paidOutThisMonth), sub: "current month" },
          ].map((tile) => (
            <div key={tile.label} className="rounded-lg border p-4">
              <div className="text-xs text-muted-foreground">{tile.label}</div>
              <div className="text-xl font-bold mt-1">{tile.value}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{tile.sub}</div>
            </div>
          ))}
        </div>
      )}

      {/* Monthly Trend AreaChart */}
      {data && data.monthlyTrend.length > 0 && (
        <div className="rounded-lg border p-4">
          <div className="text-sm font-medium mb-3">Monthly Trend — Last 12 Months</div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={data.monthlyTrend} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="inflowGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="outflowGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => "$" + (v / 1000).toFixed(0) + "k"} />
              <Tooltip formatter={(v: number) => fmt(v)} />
              <Legend />
              <Area type="monotone" dataKey="inflow" name="Inflow" stroke="#6366f1" fill="url(#inflowGrad)" strokeWidth={2} />
              <Area type="monotone" dataKey="outflow" name="Outflow" stroke="#ef4444" fill="url(#outflowGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Pool-by-Pool Table */}
      {data && (
        <div className="rounded-lg border overflow-hidden">
          <div className="text-sm font-medium p-4 border-b flex items-center gap-2">
            <BarChart3 className="h-4 w-4" /> Pool Breakdown
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0">
                <tr>
                  {["Pool", "Status", "Balance", "Threshold", "% Funded", "Donated This Month", "Total Paid Out"].map((h) => (
                    <th key={h} className="px-4 py-2 text-left font-medium text-muted-foreground whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(data.pools ?? []).map((p) => (
                  <tr key={p.id} className="border-t">
                    <td className="px-4 py-2 font-medium">{p.name}</td>
                    <td className="px-4 py-2">
                      <Badge variant={p.status === "active" ? "default" : "secondary"}>{p.status}</Badge>
                    </td>
                    <td className="px-4 py-2 font-mono">{fmt(p.balance)}</td>
                    <td className="px-4 py-2 font-mono">{fmt(p.threshold)}</td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-muted rounded-full h-1.5 min-w-[60px]">
                          <div
                            className="h-1.5 rounded-full bg-primary"
                            style={{ width: `${Math.min(100, p.percentFunded)}%` }}
                          />
                        </div>
                        <span className="text-xs text-muted-foreground">{p.percentFunded.toFixed(1)}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-2 font-mono">{fmt(p.donatedThisMonth)}</td>
                    <td className="px-4 py-2 font-mono">{fmt(p.totalPaidOut)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Cash Flow Chart + Table */}
      {data && data.cashFlow.length > 0 && (
        <div className="rounded-lg border overflow-hidden">
          <div className="text-sm font-medium p-4 border-b">Cash Flow — Last 12 Weeks</div>
          <div className="p-4">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={data.cashFlow} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="week" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => "$" + (v / 1000).toFixed(0) + "k"} />
                <Tooltip formatter={(v: number) => fmt(v)} />
                <Legend />
                <Bar dataKey="inflow" name="Inflow" fill="#6366f1" radius={[2, 2, 0, 0]} />
                <Bar dataKey="outflow" name="Outflow" fill="#ef4444" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="overflow-x-auto border-t">
            <table className="w-full text-xs">
              <thead className="">
                <tr>
                  {["Week", "Inflow", "Outflow", "Net"].map((h) => (
                    <th key={h} className="px-4 py-2 text-left font-medium text-muted-foreground">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.cashFlow.map((r) => {
                  const net = r.inflow - r.outflow;
                  return (
                    <tr key={r.week} className="border-t">
                      <td className="px-4 py-1.5">{r.week}</td>
                      <td className="px-4 py-1.5 font-mono text-green-600">{fmt(r.inflow)}</td>
                      <td className="px-4 py-1.5 font-mono text-red-600">{fmt(r.outflow)}</td>
                      <td className={`px-4 py-1.5 font-mono font-medium ${net >= 0 ? "text-green-600" : "text-red-600"}`}>{fmt(net)}</td>
                    </tr>
                  );
                })}
                <tr className="border-t font-medium">
                  <td className="px-4 py-1.5">Total</td>
                  <td className="px-4 py-1.5 font-mono text-green-600">{fmt(data.cashFlow.reduce((s, r) => s + r.inflow, 0))}</td>
                  <td className="px-4 py-1.5 font-mono text-red-600">{fmt(data.cashFlow.reduce((s, r) => s + r.outflow, 0))}</td>
                  <td className="px-4 py-1.5 font-mono">
                    {fmt(data.cashFlow.reduce((s, r) => s + r.inflow - r.outflow, 0))}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sheet 2: Donation Ledger ───────────────────────────────────────────────────

interface DonationRow {
  id: number; amount: string; isRecurring: boolean; status: string;
  stripeSessionId: string | null; createdAt: string;
  donorName: string | null;
  poolName: string | null; poolId: number;
}

interface DonationsData {
  rows: DonationRow[];
  total: number; page: number; pages: number;
  summary: { total: string; count: number; average: string };
}

interface PoolOption { id: number; name: string }

type SortColumn = "date" | "amount" | "pool" | "status";

function SortHeader({ col, label, sortBy, sortDir, onSort }: {
  col: SortColumn; label: string; sortBy: SortColumn; sortDir: "asc" | "desc";
  onSort: (col: SortColumn) => void;
}) {
  const active = sortBy === col;
  return (
    <th
      className="px-4 py-2 text-left font-medium text-muted-foreground whitespace-nowrap cursor-pointer select-none hover:text-foreground"
      onClick={() => onSort(col)}
    >
      {label}{active ? (sortDir === "asc" ? " ↑" : " ↓") : " ⇅"}
    </th>
  );
}

function DonationsSheet({ pools }: { pools?: PoolOption[] }) {
  const [page, setPage] = useState(1);
  const [poolFilter, setPoolFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [recurringFilter, setRecurringFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortBy, setSortBy] = useState<SortColumn>("date");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const handleSort = (col: SortColumn) => {
    if (sortBy === col) { setSortDir(sortDir === "asc" ? "desc" : "asc"); }
    else { setSortBy(col); setSortDir("desc"); }
    setPage(1);
  };

  const params = new URLSearchParams({ page: String(page), sortBy, sortDir });
  if (poolFilter !== "all") params.set("poolId", poolFilter);
  if (statusFilter !== "all") params.set("status", statusFilter);
  if (recurringFilter !== "all") params.set("recurring", recurringFilter);
  if (dateFrom) params.set("dateFrom", dateFrom);
  if (dateTo) params.set("dateTo", dateTo);

  const { data, isFetching, dataUpdatedAt } = useQuery<DonationsData>({
    queryKey: ["analytics-donations", page, poolFilter, statusFilter, recurringFilter, dateFrom, dateTo, sortBy, sortDir],
    queryFn: () =>
      fetch(`/api/admin/analytics/donations?${params}`, { credentials: "include" }).then((r) => r.json()),
    refetchInterval: 60000,
  });

  const handleExport = useCallback(() => {
    if (!data) return;
    exportCsv("donations.csv",
      ["ID", "Donor", "Pool", "Amount", "Recurring", "Status", "Stripe Session", "Date"],
      (data.rows ?? []).map((r) => [
        r.id, r.donorName ?? "Anonymous", r.poolName ?? "—",
        r.amount, r.isRecurring ? "Yes" : "No", r.status,
        r.stripeSessionId ?? "—", shortDate(r.createdAt),
      ]),
    );
  }, [data]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={poolFilter} onValueChange={(v) => { setPoolFilter(v); setPage(1); }}>
          <SelectTrigger className="w-40 h-8 text-xs"><SelectValue placeholder="All Pools" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Pools</SelectItem>
            {(pools ?? []).map((p) => <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-36 h-8 text-xs"><SelectValue placeholder="All Statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={recurringFilter} onValueChange={(v) => { setRecurringFilter(v); setPage(1); }}>
          <SelectTrigger className="w-36 h-8 text-xs"><SelectValue placeholder="All Types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="true">Recurring</SelectItem>
            <SelectItem value="false">One-time</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-muted-foreground">From</span>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
            className="h-8 px-2 text-xs rounded-md border border-input bg-background focus:outline-none focus:ring-1 focus:ring-ring"
          />
          <span className="text-xs text-muted-foreground">To</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
            className="h-8 px-2 text-xs rounded-md border border-input bg-background focus:outline-none focus:ring-1 focus:ring-ring"
          />
          {(dateFrom || dateTo) && (
            <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={() => { setDateFrom(""); setDateTo(""); setPage(1); }}>
              Clear
            </Button>
          )}
        </div>
        <div className="ml-auto flex items-center gap-3">
          <LastUpdated dataUpdatedAt={dataUpdatedAt ?? 0} isFetching={isFetching} />
          <Button variant="outline" size="sm" onClick={handleExport} disabled={!data}>
            <Download className="h-4 w-4 mr-1" /> Export CSV
          </Button>
        </div>
      </div>

      {data?.summary && (
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: "Total", value: fmt(data.summary.total) },
            { label: "Count", value: fmtNum(data.summary.count) },
            { label: "Average", value: fmt(data.summary.average) },
          ].map((t) => (
            <div key={t.label} className="rounded-lg border p-3 text-center">
              <div className="text-xs text-muted-foreground">{t.label}</div>
              <div className="text-lg font-bold mt-0.5">{t.value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-lg border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0">
              <tr>
                <SortHeader col="date" label="Date" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                <th className="px-4 py-2 text-left font-medium text-muted-foreground whitespace-nowrap">Donor</th>
                <SortHeader col="pool" label="Pool" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                <SortHeader col="amount" label="Amount" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                <th className="px-4 py-2 text-left font-medium text-muted-foreground whitespace-nowrap">Recurring</th>
                <SortHeader col="status" label="Status" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                <th className="px-4 py-2 text-left font-medium text-muted-foreground whitespace-nowrap">Stripe Session</th>
              </tr>
            </thead>
            <tbody>
              {(data?.rows ?? []).map((r) => (
                <tr key={r.id} className="border-t">
                  <td className="px-4 py-2 whitespace-nowrap">{shortDate(r.createdAt)}</td>
                  <td className="px-4 py-2 max-w-[140px] truncate">{r.donorName ?? "Anonymous"}</td>
                  <td className="px-4 py-2 max-w-[120px] truncate">{r.poolName ?? "—"}</td>
                  <td className="px-4 py-2 font-mono">{fmt(r.amount)}</td>
                  <td className="px-4 py-2">{r.isRecurring ? <Badge variant="secondary">Recurring</Badge> : "One-time"}</td>
                  <td className="px-4 py-2">
                    <Badge variant={r.status === "completed" ? "default" : r.status === "failed" ? "destructive" : "secondary"}>
                      {r.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-2 font-mono text-xs max-w-[120px] truncate">{r.stripeSessionId ?? "—"}</td>
                </tr>
              ))}
              {!isFetching && data?.rows?.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No donations found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <Pagination page={data?.page ?? 1} pages={data?.pages ?? 1} onChange={setPage} />
    </div>
  );
}

// ── Sheet 3: Debt Queue Analysis ───────────────────────────────────────────────

interface DebtQueueRow {
  id: number; creditor: string; balance: string; status: string;
  verificationStatus: string; verificationScore: number | null;
  createdAt: string; daysWaiting: number;
  debtorName: string | null; poolName: string | null; poolId: number | null;
}

interface DebtQueueData {
  rows: DebtQueueRow[];
  total: number; page: number; pages: number;
  summary: { totalBalance: string; avgWaitDays: number; oldestDate: string | null };
}

function waitColor(days: number): string {
  if (days > 90) return "text-red-600 dark:text-red-400";
  if (days > 30) return "text-yellow-600 dark:text-yellow-400";
  return "text-green-600 dark:text-green-400";
}

function DebtQueueSheet() {
  const [page, setPage] = useState(1);

  const { data, isFetching, dataUpdatedAt } = useQuery<DebtQueueData>({
    queryKey: ["analytics-debt-queue", page],
    queryFn: () =>
      fetch(`/api/admin/analytics/debt-queue?page=${page}`, { credentials: "include" }).then((r) => r.json()),
    refetchInterval: 60000,
  });

  const handleExport = useCallback(() => {
    if (!data) return;
    exportCsv("debt-queue.csv",
      ["ID", "Debtor", "Pool", "Creditor", "Balance", "Status", "Verification", "Score", "Days Waiting", "Submitted"],
      (data.rows ?? []).map((r) => [
        r.id, r.debtorName ?? "Anon", r.poolName ?? "—", r.creditor, r.balance,
        r.status, r.verificationStatus, r.verificationScore ?? "—",
        r.daysWaiting, shortDate(r.createdAt),
      ]),
    );
  }, [data]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <LastUpdated dataUpdatedAt={dataUpdatedAt ?? 0} isFetching={isFetching} />
        <Button variant="outline" size="sm" onClick={handleExport} disabled={!data}>
          <Download className="h-4 w-4 mr-1" /> Export CSV
        </Button>
      </div>

      {data?.summary && (
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: "Total Debt Awaiting Payoff", value: fmt(data.summary.totalBalance) },
            { label: "Avg Wait Time", value: `${data.summary.avgWaitDays} days` },
            { label: "Oldest Submission", value: shortDate(data.summary.oldestDate) },
          ].map((t) => (
            <div key={t.label} className="rounded-lg border p-3 text-center">
              <div className="text-xs text-muted-foreground">{t.label}</div>
              <div className="text-lg font-bold mt-0.5">{t.value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-green-500 inline-block" /> &lt;30 days</span>
        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-yellow-500 inline-block" /> 30–90 days</span>
        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-red-500 inline-block" /> &gt;90 days</span>
      </div>

      <div className="rounded-lg border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0">
              <tr>
                {["Debtor", "Pool", "Creditor", "Balance", "Status", "Verification", "Score", "Days Waiting"].map((h) => (
                  <th key={h} className="px-4 py-2 text-left font-medium text-muted-foreground whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data?.rows ?? []).map((r) => (
                <tr key={r.id} className="border-t">
                  <td className="px-4 py-2 max-w-[120px] truncate">{r.debtorName ?? "Anon"}</td>
                  <td className="px-4 py-2 max-w-[100px] truncate">{r.poolName ?? "—"}</td>
                  <td className="px-4 py-2 max-w-[120px] truncate">{r.creditor}</td>
                  <td className="px-4 py-2 font-mono">{fmt(r.balance)}</td>
                  <td className="px-4 py-2"><Badge variant="secondary">{r.status}</Badge></td>
                  <td className="px-4 py-2">
                    <Badge variant={
                      r.verificationStatus === "verified" ? "default" :
                      r.verificationStatus === "flagged" || r.verificationStatus === "manual_review" ? "destructive" : "secondary"
                    }>{r.verificationStatus}</Badge>
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">{r.verificationScore ?? "—"}</td>
                  <td className={`px-4 py-2 font-medium ${waitColor(r.daysWaiting)}`}>{r.daysWaiting}d</td>
                </tr>
              ))}
              {!isFetching && data?.rows?.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">No pending or queued debts</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <Pagination page={data?.page ?? 1} pages={data?.pages ?? 1} onChange={setPage} />
    </div>
  );
}

// ── Sheet 4: Payout History ────────────────────────────────────────────────────

interface PayoutRow {
  id: number; totalAmount: string; paymentMethod: string;
  reference: string | null; status: string; executedBy: string;
  executedAt: string | null; createdAt: string; debtCount: number;
  poolName: string | null; poolId: number;
}

interface PayoutsData {
  rows: PayoutRow[];
  total: number; page: number; pages: number;
}

interface PayoutBatchDetail {
  batch: PayoutRow;
  debts: Array<{
    id: number; creditor: string; balance: string; status: string;
    paymentSentAt: string | null; paymentReference: string | null;
    debtorName: string | null;
  }>;
}

function PayoutsSheet() {
  const [page, setPage] = useState(1);
  const [drilldown, setDrilldown] = useState<number | null>(null);

  const { data, isFetching, dataUpdatedAt } = useQuery<PayoutsData>({
    queryKey: ["analytics-payouts", page],
    queryFn: () =>
      fetch(`/api/admin/analytics/payouts?page=${page}`, { credentials: "include" }).then((r) => r.json()),
    refetchInterval: 60000,
    enabled: drilldown === null,
  });

  const { data: detail, isFetching: detailFetching } = useQuery<PayoutBatchDetail>({
    queryKey: ["analytics-payout-detail", drilldown],
    queryFn: () =>
      fetch(`/api/admin/analytics/payouts?batchId=${drilldown}`, { credentials: "include" }).then((r) => r.json()),
    enabled: drilldown !== null,
  });

  const handleExport = useCallback(() => {
    if (drilldown !== null && detail) {
      exportCsv(`payout-batch-${drilldown}-debts.csv`,
        ["ID", "Debtor", "Creditor", "Balance", "Status", "Payment Sent", "Reference"],
        (detail.debts ?? []).map((d) => [
          d.id, d.debtorName ?? "Anon", d.creditor, d.balance,
          d.status, shortDate(d.paymentSentAt), d.paymentReference ?? "—",
        ]),
      );
    } else if (data) {
      exportCsv("payout-history.csv",
        ["ID", "Pool", "Date", "Debts", "Total Amount", "Payment Method", "Reference", "Status"],
        (data.rows ?? []).map((r) => [
          r.id, r.poolName ?? "—", shortDate(r.createdAt), r.debtCount,
          r.totalAmount, r.paymentMethod, r.reference ?? "—", r.status,
        ]),
      );
    }
  }, [data, detail, drilldown]);

  if (drilldown !== null && detail) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => setDrilldown(null)}>
            <ChevronLeft className="h-4 w-4 mr-1" /> Back to History
          </Button>
          <span className="text-sm font-medium">Batch #{drilldown} — {detail.batch.poolName} · {fmt(detail.batch.totalAmount)}</span>
          <div className="ml-auto flex items-center gap-2">
            <Badge variant={detail.batch.status === "confirmed" ? "default" : "secondary"}>{detail.batch.status}</Badge>
            <Button variant="outline" size="sm" onClick={handleExport}>
              <Download className="h-4 w-4 mr-1" /> Export CSV
            </Button>
          </div>
        </div>
        <div className="rounded-lg border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0">
                <tr>
                  {["Debtor", "Creditor", "Balance", "Status", "Payment Sent", "Reference"].map((h) => (
                    <th key={h} className="px-4 py-2 text-left font-medium text-muted-foreground whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(detail.debts ?? []).map((d) => (
                  <tr key={d.id} className="border-t">
                    <td className="px-4 py-2">{d.debtorName ?? "Anon"}</td>
                    <td className="px-4 py-2">{d.creditor}</td>
                    <td className="px-4 py-2 font-mono">{fmt(d.balance)}</td>
                    <td className="px-4 py-2"><Badge variant="secondary">{d.status}</Badge></td>
                    <td className="px-4 py-2">{shortDate(d.paymentSentAt)}</td>
                    <td className="px-4 py-2 font-mono text-xs">{d.paymentReference ?? "—"}</td>
                  </tr>
                ))}
                {!detailFetching && detail.debts?.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No individual debt records</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <LastUpdated dataUpdatedAt={dataUpdatedAt ?? 0} isFetching={isFetching} />
        <Button variant="outline" size="sm" onClick={handleExport} disabled={!data}>
          <Download className="h-4 w-4 mr-1" /> Export CSV
        </Button>
      </div>
      <div className="rounded-lg border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0">
              <tr>
                {["Date", "Pool", "Debts", "Total Amount", "Method", "Reference", "Status", ""].map((h) => (
                  <th key={h} className="px-4 py-2 text-left font-medium text-muted-foreground whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data?.rows ?? []).map((r) => (
                <tr key={r.id} className="border-t">
                  <td className="px-4 py-2 whitespace-nowrap">{shortDate(r.createdAt)}</td>
                  <td className="px-4 py-2 max-w-[120px] truncate">{r.poolName ?? "—"}</td>
                  <td className="px-4 py-2">{r.debtCount ?? 0}</td>
                  <td className="px-4 py-2 font-mono">{fmt(r.totalAmount)}</td>
                  <td className="px-4 py-2 uppercase text-xs">{r.paymentMethod}</td>
                  <td className="px-4 py-2 font-mono text-xs">{r.reference ?? "—"}</td>
                  <td className="px-4 py-2">
                    <Badge variant={r.status === "confirmed" ? "default" : r.status === "failed" ? "destructive" : "secondary"}>
                      {r.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-2">
                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setDrilldown(r.id)}>
                      View →
                    </Button>
                  </td>
                </tr>
              ))}
              {!isFetching && data?.rows?.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">No payout batches yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <Pagination page={data?.page ?? 1} pages={data?.pages ?? 1} onChange={setPage} />
    </div>
  );
}

// ── Sheet 5: User Metrics ──────────────────────────────────────────────────────

interface UserMetricsData {
  totalUsers: number;
  donors: { total: number; newThisMonth: number };
  debtors: { total: number; newThisMonth: number };
  admins: { total: number; newThisMonth: number };
  roleBreakdown: Array<{ name: string; value: number }>;
  weeklySignups: Array<{ week: string; count: number }>;
  donorRetention: { multiDonorCount: number; totalDonorCount: number; retentionPct: number };
}

const PIE_COLORS = ["#6366f1", "#22c55e", "#f59e0b"];

interface AdminUserRow {
  id: number;
  email: string;
  displayName: string | null;
  role: string;
  isAdmin: boolean;
  isBoard: boolean;
  registrationIp: string | null;
  deviceFingerprintHash: string | null;
  roleConfirmedAt: string | null;
  createdAt: string;
}

interface AdminUsersResponse {
  users: AdminUserRow[];
  total: number;
  limit: number;
  offset: number;
}

function UsersSheet() {
  const { data, isFetching, dataUpdatedAt } = useQuery<UserMetricsData>({
    queryKey: ["analytics-users"],
    queryFn: () => fetch("/api/admin/analytics/users", { credentials: "include" }).then((r) => r.json()),
    refetchInterval: 60000,
  });

  const [regPage, setRegPage] = useState(0);
  const REG_PAGE_SIZE = 25;
  const { data: regData, isFetching: regFetching } = useQuery<AdminUsersResponse>({
    queryKey: ["admin-users-list", regPage],
    queryFn: () =>
      fetch(`/api/admin/users?limit=${REG_PAGE_SIZE}&offset=${regPage * REG_PAGE_SIZE}`, { credentials: "include" })
        .then((r) => r.json()),
    refetchInterval: 60000,
  });

  const qc = useQueryClient();
  const [boardTogglingId, setBoardTogglingId] = useState<number | null>(null);
  const boardToggleMutation = useMutation({
    mutationFn: async ({ id, isBoard }: { id: number; isBoard: boolean }) => {
      setBoardTogglingId(id);
      const r = await fetch(`/api/admin/users/${id}/board`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isBoard }),
      });
      if (!r.ok) throw new Error(await r.text());
      return r.json();
    },
    onSettled: () => {
      setBoardTogglingId(null);
      void qc.invalidateQueries({ queryKey: ["admin-users-list"] });
    },
  });

  const handleExport = useCallback(() => {
    if (!data) return;
    exportCsv("weekly-signups.csv",
      ["Week", "New Signups"],
      (data.weeklySignups ?? []).map((r) => [r.week, r.count]),
    );
  }, [data]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <LastUpdated dataUpdatedAt={dataUpdatedAt ?? 0} isFetching={isFetching} />
        <Button variant="outline" size="sm" onClick={handleExport} disabled={!data}>
          <Download className="h-4 w-4 mr-1" /> Export CSV
        </Button>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: "Total Users", value: fmtNum(data.totalUsers), sub: "all time" },
              { label: "Donors", value: fmtNum(data.donors.total), sub: `+${data.donors.newThisMonth} this month` },
              { label: "Debtors", value: fmtNum(data.debtors.total), sub: `+${data.debtors.newThisMonth} this month` },
              { label: "Donor Retention", value: `${data.donorRetention.retentionPct}%`, sub: "gave more than once" },
            ].map((t) => (
              <div key={t.label} className="rounded-lg border p-4">
                <div className="text-xs text-muted-foreground">{t.label}</div>
                <div className="text-xl font-bold mt-1">{t.value}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{t.sub}</div>
              </div>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Role Donut */}
            <div className="rounded-lg border p-4">
              <div className="text-sm font-medium mb-3">Role Breakdown</div>
              <div className="flex items-center gap-4">
                <ResponsiveContainer width="60%" height={180}>
                  <PieChart>
                    <Pie data={data.roleBreakdown} cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={3} dataKey="value">
                      {data.roleBreakdown.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v: number) => fmtNum(v)} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-2 text-sm">
                  {data.roleBreakdown.map((r, i) => (
                    <div key={r.name} className="flex items-center gap-2">
                      <span className="h-3 w-3 rounded-sm inline-block" style={{ background: PIE_COLORS[i] }} />
                      <span>{r.name}: <strong>{fmtNum(r.value)}</strong></span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Weekly signups */}
            <div className="rounded-lg border p-4">
              <div className="text-sm font-medium mb-3">New Sign-ups — Last 12 Weeks</div>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={data.weeklySignups} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="week" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" name="Signups" fill="#6366f1" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}

      {/* ── Registration Detail Table ─────────────────────────────────────── */}
      <div className="rounded-lg border">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="text-sm font-medium">User Registrations — IP &amp; Device Fingerprint</div>
          {regFetching && <span className="text-xs text-muted-foreground">Refreshing…</span>}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b">
                <th className="text-left px-3 py-2 font-medium text-muted-foreground">ID</th>
                <th className="text-left px-3 py-2 font-medium text-muted-foreground">Email</th>
                <th className="text-left px-3 py-2 font-medium text-muted-foreground">Role</th>
                <th className="text-left px-3 py-2 font-medium text-muted-foreground">Registration IP</th>
                <th className="text-left px-3 py-2 font-medium text-muted-foreground">Device Fingerprint</th>
                <th className="text-left px-3 py-2 font-medium text-muted-foreground">Joined</th>
                <th className="text-left px-3 py-2 font-medium text-muted-foreground">Board</th>
              </tr>
            </thead>
            <tbody>
              {(regData?.users ?? []).map((u) => (
                <tr key={u.id} className="border-b last:border-0">
                  <td className="px-3 py-2 text-muted-foreground">#{u.id}</td>
                  <td className="px-3 py-2">
                    <a href={`mailto:${u.email}`} className="text-blue-600 hover:underline">{u.email}</a>
                    {u.isAdmin && <span className="ml-1 text-amber-600 font-medium">[admin]</span>}
                    {u.isBoard && <span className="ml-1 text-violet-600 font-medium">[board]</span>}
                  </td>
                  <td className="px-3 py-2 capitalize">{u.role}</td>
                  <td className="px-3 py-2">
                    {u.registrationIp
                      ? <code className="bg-muted px-1 rounded">{u.registrationIp}</code>
                      : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-3 py-2">
                    {u.deviceFingerprintHash
                      ? <code className="bg-muted px-1 rounded font-mono">{u.deviceFingerprintHash}</code>
                      : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {new Date(u.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  </td>
                  <td className="px-3 py-2">
                    <label className="flex items-center gap-1.5 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        className="accent-violet-600 h-3.5 w-3.5"
                        checked={!!u.isBoard}
                        disabled={boardTogglingId === u.id}
                        onChange={() => boardToggleMutation.mutate({ id: u.id, isBoard: !u.isBoard })}
                      />
                      <span className={`text-xs font-medium ${u.isBoard ? "text-violet-700" : "text-muted-foreground"}`}>
                        {boardTogglingId === u.id ? "…" : "Board"}
                      </span>
                    </label>
                  </td>
                </tr>
              ))}
              {!regData && (
                <tr><td colSpan={7} className="px-3 py-4 text-center text-muted-foreground">Loading…</td></tr>
              )}
              {regData && regData.users.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-4 text-center text-muted-foreground">No users found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {regData && regData.total > REG_PAGE_SIZE && (
          <div className="flex items-center justify-between px-4 py-2 border-t text-xs text-muted-foreground">
            <span>
              Showing {regPage * REG_PAGE_SIZE + 1}–{Math.min((regPage + 1) * REG_PAGE_SIZE, regData.total)} of {regData.total} users
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setRegPage((p) => Math.max(0, p - 1))}
                disabled={regPage === 0}
                className="px-2 py-1 rounded border disabled:opacity-40 hover:bg-muted"
              >‹ Prev</button>
              <button
                onClick={() => setRegPage((p) => p + 1)}
                disabled={(regPage + 1) * REG_PAGE_SIZE >= regData.total}
                className="px-2 py-1 rounded border disabled:opacity-40 hover:bg-muted"
              >Next ›</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Sheet 6: Pool Performance ──────────────────────────────────────────────────

interface PoolPerfRow {
  id: number; name: string; status: string;
  balance: string; threshold: string; percentFunded: number;
  velocityPerDay: string; donated30d: string;
  projectedDays: number | null; debtCount: number; totalDebtWaiting: string;
}

interface PoolsData {
  rows: PoolPerfRow[];
  total: number; page: number; pages: number;
}

function PoolsSheet() {
  const [page, setPage] = useState(1);

  const { data, isFetching, dataUpdatedAt } = useQuery<PoolsData>({
    queryKey: ["analytics-pools", page],
    queryFn: () => fetch(`/api/admin/analytics/pools?page=${page}`, { credentials: "include" }).then((r) => r.json()),
    refetchInterval: 60000,
  });

  const handleExport = useCallback(() => {
    if (!data) return;
    exportCsv("pool-performance.csv",
      ["Pool", "Status", "% Funded", "Balance", "Threshold", "Velocity $/day", "Donated (30d)", "Projected Days", "Debts Waiting", "Total Debt Waiting"],
      (data.rows ?? []).map((r) => [
        r.name, r.status, r.percentFunded.toFixed(1) + "%", r.balance, r.threshold,
        r.velocityPerDay, r.donated30d, r.projectedDays ?? "N/A", r.debtCount, r.totalDebtWaiting,
      ]),
    );
  }, [data]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <LastUpdated dataUpdatedAt={dataUpdatedAt ?? 0} isFetching={isFetching} />
        <Button variant="outline" size="sm" onClick={handleExport} disabled={!data}>
          <Download className="h-4 w-4 mr-1" /> Export CSV
        </Button>
      </div>

      <div className="rounded-lg border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0">
              <tr>
                {["Pool", "Status", "% Funded", "Balance", "Threshold", "Velocity $/day", "Donated (30d)", "Projected Days to Full", "Debts Waiting", "Total Debt Waiting"].map((h) => (
                  <th key={h} className="px-4 py-2 text-left font-medium text-muted-foreground whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data?.rows ?? []).map((r) => (
                <tr key={r.id} className="border-t">
                  <td className="px-4 py-2 font-medium">{r.name}</td>
                  <td className="px-4 py-2"><Badge variant={r.status === "active" ? "default" : "secondary"}>{r.status}</Badge></td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <div className="w-16 bg-muted rounded-full h-1.5">
                        <div className="h-1.5 rounded-full bg-primary" style={{ width: `${Math.min(100, r.percentFunded)}%` }} />
                      </div>
                      <span className="text-xs">{r.percentFunded.toFixed(1)}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-2 font-mono">{fmt(r.balance)}</td>
                  <td className="px-4 py-2 font-mono">{fmt(r.threshold)}</td>
                  <td className="px-4 py-2 font-mono">{fmt(r.velocityPerDay, 2)}</td>
                  <td className="px-4 py-2 font-mono">{fmt(r.donated30d)}</td>
                  <td className="px-4 py-2">
                    {r.projectedDays != null ? (
                      <span className={r.projectedDays < 30 ? "text-green-600 font-medium" : r.projectedDays < 90 ? "text-yellow-600" : "text-muted-foreground"}>
                        {r.projectedDays}d
                      </span>
                    ) : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-4 py-2">{fmtNum(r.debtCount)}</td>
                  <td className="px-4 py-2 font-mono">{fmt(r.totalDebtWaiting)}</td>
                </tr>
              ))}
              {!isFetching && data?.rows?.length === 0 && (
                <tr><td colSpan={10} className="px-4 py-8 text-center text-muted-foreground">No pools found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <Pagination page={data?.page ?? 1} pages={data?.pages ?? 1} onChange={setPage} />
    </div>
  );
}

// ── Sheet 8: Job Queue Monitor ─────────────────────────────────────────────

interface JobSummary {
  completed24h: number; failed24h: number; total24h: number;
  successRate: number | null; avgProcessingSeconds: number | null;
}
interface JobTypeCount { pending: number; running: number }
interface ActiveJob { id: number; type: string; status: string; attempts: number; startedAt: string | null; createdAt: string }
interface DeadJob {
  id: number; type: string; status: string; error: string | null;
  attempts: number; maxAttempts: number; payload: unknown;
  createdAt: string; updatedAt: string;
}
interface JobsData {
  summary: JobSummary;
  byType: Record<string, JobTypeCount>;
  activeJobs: ActiveJob[];
  deadJobs: DeadJob[];
}

const JOB_TYPE_LABELS: Record<string, string> = {
  ocr_verify: "OCR Verify",
  generate_tax_cert: "Tax Cert",
  send_email: "Send Email",
  payout_execute: "Payout",
  fraud_score: "Fraud Score",
  zip_export: "ZIP Export",
};

function JobsSheet() {
  const [retrying, setRetrying] = useState<Set<number>>(new Set());
  const { data, isFetching, dataUpdatedAt, refetch } = useQuery<JobsData>({
    queryKey: ["admin-jobs"],
    queryFn: () => fetch("/api/admin/jobs", { credentials: "include" }).then((r) => r.json()),
    refetchInterval: 30000,
  });

  const handleRetry = useCallback(async (jobId: number) => {
    setRetrying((prev) => new Set(prev).add(jobId));
    try {
      const res = await fetch(`/api/admin/jobs/${jobId}/retry`, {
        method: "POST", credentials: "include",
      });
      if (!res.ok) throw new Error("Retry failed");
      await refetch();
    } catch {
      // error handled by removing from retrying set
    } finally {
      setRetrying((prev) => { const s = new Set(prev); s.delete(jobId); return s; });
    }
  }, [refetch]);

  const summary = data?.summary;
  const byType = data?.byType ?? {};
  const totalPending = Object.values(byType).reduce((s, v) => s + v.pending, 0);
  const totalRunning = Object.values(byType).reduce((s, v) => s + v.running, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Job Queue Monitor</h2>
          <LastUpdated dataUpdatedAt={dataUpdatedAt ?? 0} isFetching={isFetching} />
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`h-4 w-4 mr-1.5 ${isFetching ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="rounded-lg border p-4">
          <p className="text-xs text-muted-foreground mb-1">Pending</p>
          <p className="text-2xl font-bold">{totalPending}</p>
        </div>
        <div className="rounded-lg border p-4">
          <p className="text-xs text-muted-foreground mb-1">Running</p>
          <p className="text-2xl font-bold text-blue-600">{totalRunning}</p>
        </div>
        <div className={`rounded-lg border p-4 ${(summary?.failed24h ?? 0) > 0 ? "bg-red-50 border-red-200" : ""}`}>
          <p className="text-xs text-muted-foreground mb-1">Failed (24h)</p>
          <p className={`text-2xl font-bold ${(summary?.failed24h ?? 0) > 0 ? "text-red-600" : ""}`}>
            {summary?.failed24h ?? 0}
          </p>
        </div>
        <div className="rounded-lg border p-4">
          <p className="text-xs text-muted-foreground mb-1">Success Rate (24h)</p>
          <p className="text-2xl font-bold text-green-600">
            {summary?.successRate != null ? `${summary.successRate}%` : "—"}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div className="rounded-lg border p-3 bg-muted/20">
          <p className="text-xs text-muted-foreground">Completed (24h)</p>
          <p className="text-xl font-semibold">{summary?.completed24h ?? 0}</p>
        </div>
        <div className="rounded-lg border p-3 bg-muted/20">
          <p className="text-xs text-muted-foreground">Avg Processing Time</p>
          <p className="text-xl font-semibold">
            {summary?.avgProcessingSeconds != null ? `${summary.avgProcessingSeconds}s` : "—"}
          </p>
        </div>
        <div className="rounded-lg border p-3 bg-muted/20">
          <p className="text-xs text-muted-foreground">Dead-Letter Jobs</p>
          <p className={`text-xl font-semibold ${(data?.deadJobs?.length ?? 0) > 0 ? "text-red-600" : ""}`}>
            {data?.deadJobs?.length ?? 0}
          </p>
        </div>
      </div>

      {/* Queue by type */}
      {Object.keys(byType).length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-2">Queue by Job Type</h3>
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">Type</th>
                  <th className="px-4 py-2 text-right font-medium">Pending</th>
                  <th className="px-4 py-2 text-right font-medium">Running</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(byType).map(([type, counts]) => (
                  <tr key={type} className="border-t">
                    <td className="px-4 py-2 font-medium">{JOB_TYPE_LABELS[type] ?? type}</td>
                    <td className="px-4 py-2 text-right">{counts.pending}</td>
                    <td className="px-4 py-2 text-right text-blue-600">{counts.running}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Currently running */}
      {(data?.activeJobs?.length ?? 0) > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-2">Currently Running</h3>
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">ID</th>
                  <th className="px-4 py-2 text-left font-medium">Type</th>
                  <th className="px-4 py-2 text-right font-medium">Attempts</th>
                  <th className="px-4 py-2 text-left font-medium">Started</th>
                </tr>
              </thead>
              <tbody>
                {(data?.activeJobs ?? []).map((j) => (
                  <tr key={j.id} className="border-t">
                    <td className="px-4 py-2 text-muted-foreground">#{j.id}</td>
                    <td className="px-4 py-2">
                      <Badge variant="secondary">{JOB_TYPE_LABELS[j.type] ?? j.type}</Badge>
                    </td>
                    <td className="px-4 py-2 text-right">{j.attempts}</td>
                    <td className="px-4 py-2 text-muted-foreground">
                      {j.startedAt ? new Date(j.startedAt).toLocaleTimeString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Dead-letter queue */}
      <div>
        <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
          <XCircle className="h-4 w-4 text-red-500" />
          Dead-Letter Queue
        </h3>
        {(data?.deadJobs?.length ?? 0) === 0 ? (
          <div className="rounded-md border p-6 text-center text-muted-foreground text-sm">
            <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500 opacity-60" />
            No dead-letter jobs — queue is healthy
          </div>
        ) : (
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">ID</th>
                  <th className="px-4 py-2 text-left font-medium">Type</th>
                  <th className="px-4 py-2 text-right font-medium">Attempts</th>
                  <th className="px-4 py-2 text-left font-medium">Error</th>
                  <th className="px-4 py-2 text-left font-medium">Failed At</th>
                  <th className="px-4 py-2 text-center font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {(data?.deadJobs ?? []).map((j) => (
                  <tr key={j.id} className="border-t">
                    <td className="px-4 py-2 text-muted-foreground">#{j.id}</td>
                    <td className="px-4 py-2">
                      <Badge variant="destructive">{JOB_TYPE_LABELS[j.type] ?? j.type}</Badge>
                    </td>
                    <td className="px-4 py-2 text-right">{j.attempts}/{j.maxAttempts}</td>
                    <td className="px-4 py-2 max-w-[240px] truncate text-red-700 text-xs" title={j.error ?? ""}>
                      {j.error ?? "Unknown error"}
                    </td>
                    <td className="px-4 py-2 text-muted-foreground whitespace-nowrap">
                      {new Date(j.updatedAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-2 text-center">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRetry(j.id)}
                        disabled={retrying.has(j.id)}
                        className="text-xs h-7"
                      >
                        <RotateCcw className={`h-3 w-3 mr-1 ${retrying.has(j.id) ? "animate-spin" : ""}`} />
                        Retry
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Sheet 7: Fraud & Verification ─────────────────────────────────────────────

interface FraudRow {
  id: number; creditor: string; balance: string; status: string;
  verificationStatus: string; verificationScore: number | null;
  verificationNotes: string | null; verificationOverrideReason: string | null;
  createdAt: string; debtorName: string | null; poolName: string | null;
}

interface FraudData {
  rows: FraudRow[];
  total: number; page: number; pages: number;
  fraudStats: { totalThisMonth: number; flaggedThisMonth: number; fraudRate: number };
  topFlaggedCreditors: Array<{ creditor: string; cnt: number }>;
}

function FraudSheet() {
  const [page, setPage] = useState(1);

  const { data, isFetching, dataUpdatedAt } = useQuery<FraudData>({
    queryKey: ["analytics-fraud", page],
    queryFn: () =>
      fetch(`/api/admin/analytics/fraud?page=${page}`, { credentials: "include" }).then((r) => r.json()),
    refetchInterval: 60000,
  });

  const handleExport = useCallback(() => {
    if (!data) return;
    exportCsv("fraud-verification.csv",
      ["ID", "Debtor", "Pool", "Creditor", "Balance", "Status", "Verification Status", "Score", "Notes", "Submitted"],
      (data.rows ?? []).map((r) => [
        r.id, r.debtorName ?? "Anon", r.poolName ?? "—", r.creditor, r.balance,
        r.status, r.verificationStatus, r.verificationScore ?? "—",
        r.verificationNotes ?? "—", shortDate(r.createdAt),
      ]),
    );
  }, [data]);

  const isHighRisk = (row: FraudRow) =>
    row.verificationStatus === "flagged" || row.verificationStatus === "manual_review" ||
    (row.verificationScore != null && row.verificationScore < 40);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <LastUpdated dataUpdatedAt={dataUpdatedAt ?? 0} isFetching={isFetching} />
        <Button variant="outline" size="sm" onClick={handleExport} disabled={!data}>
          <Download className="h-4 w-4 mr-1" /> Export CSV
        </Button>
      </div>

      {data?.fraudStats && (
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: "Submissions This Month", value: fmtNum(data.fraudStats.totalThisMonth) },
            { label: "Flagged This Month", value: fmtNum(data.fraudStats.flaggedThisMonth) },
            { label: "Fraud Rate", value: `${data.fraudStats.fraudRate}%` },
          ].map((t) => (
            <div key={t.label} className={`rounded-lg border p-3 text-center ${t.label === "Fraud Rate" && data.fraudStats.fraudRate > 10 ? "border-red-300 bg-red-50 dark:bg-red-950/20" : ""}`}>
              <div className="text-xs text-muted-foreground">{t.label}</div>
              <div className={`text-lg font-bold mt-0.5 ${t.label === "Fraud Rate" && data.fraudStats.fraudRate > 10 ? "text-red-600" : ""}`}>{t.value}</div>
            </div>
          ))}
        </div>
      )}

      {data?.topFlaggedCreditors && data.topFlaggedCreditors.length > 0 && (
        <div className="rounded-lg border p-4">
          <div className="text-sm font-medium mb-2 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" /> Top Flagged Creditors
          </div>
          <div className="flex flex-wrap gap-2">
            {data.topFlaggedCreditors.map((c) => (
              <Badge key={c.creditor} variant="destructive" className="gap-1">
                {c.creditor} <span className="opacity-70">({c.cnt})</span>
              </Badge>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-lg border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0">
              <tr>
                {["Debtor", "Pool", "Creditor", "Balance", "Status", "Verification", "Score", "Notes", "Submitted"].map((h) => (
                  <th key={h} className="px-4 py-2 text-left font-medium text-muted-foreground whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data?.rows ?? []).map((r) => (
                <tr key={r.id} className="border-t">
                  <td className="px-4 py-2 max-w-[110px] truncate">{r.debtorName ?? "Anon"}</td>
                  <td className="px-4 py-2 max-w-[100px] truncate">{r.poolName ?? "—"}</td>
                  <td className="px-4 py-2 max-w-[120px] truncate">{r.creditor}</td>
                  <td className="px-4 py-2 font-mono">{fmt(r.balance)}</td>
                  <td className="px-4 py-2"><Badge variant="secondary">{r.status}</Badge></td>
                  <td className="px-4 py-2">
                    <Badge variant={
                      r.verificationStatus === "verified" ? "default" :
                      r.verificationStatus === "flagged" || r.verificationStatus === "manual_review" ? "destructive" : "secondary"
                    }>{r.verificationStatus}</Badge>
                  </td>
                  <td className={`px-4 py-2 font-mono font-medium ${r.verificationScore != null && r.verificationScore < 40 ? "text-red-600" : r.verificationScore != null && r.verificationScore < 70 ? "text-yellow-600" : ""}`}>
                    {r.verificationScore ?? "—"}
                  </td>
                  <td className="px-4 py-2 max-w-[160px] truncate text-xs text-muted-foreground">{r.verificationNotes ?? r.verificationOverrideReason ?? "—"}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{shortDate(r.createdAt)}</td>
                </tr>
              ))}
              {!isFetching && data?.rows?.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">No verified submissions yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <Pagination page={data?.page ?? 1} pages={data?.pages ?? 1} onChange={setPage} />
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export default function AdminAnalytics() {
  const [, navigate] = useLocation();

  // Prefetch pool list for donation ledger filter
  const { data: poolsForFilter } = useQuery<Array<{ id: number; name: string }>>({
    queryKey: ["pools-for-filter"],
    queryFn: () => fetch("/api/pools", { credentials: "include" }).then((r) => r.json()),
    staleTime: 300000,
  });

  const sheetIcons = [DollarSign, CheckCircle, Clock, TrendingUp, Users, BarChart3, Shield, Cpu];

  return (
    <div className="min-h-screen bg-background">
      <nav className="sticky top-0 z-50 border-b bg-slate-900 border-slate-700">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white hover:bg-slate-800" onClick={() => navigate("/admin")}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Admin
            </Button>
            <LogoMark size={26} />
            <span className="font-semibold text-sm text-white">Analytics & Reports</span>
            <span className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-600 text-white tracking-wide">ADMIN</span>
          </div>
          <UserButton />
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold">Analytics & Reports</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Live platform data across 8 financial and operational views. Tables auto-refresh every 60 seconds. Job queue refreshes every 30 seconds.
          </p>
        </div>

        <Tabs defaultValue="financial">
          <TabsList className="flex flex-wrap h-auto gap-1 mb-6">
            {SHEET_LABELS.map((label, i) => {
              const Icon = sheetIcons[i];
              const value = label.toLowerCase().replace(/[^a-z]/g, "-").replace(/-+/g, "-").replace(/-$/, "");
              return (
                <TabsTrigger key={label} value={value} className="flex items-center gap-1.5 text-xs px-3 py-2">
                  <Icon className="h-3.5 w-3.5" />
                  {label}
                </TabsTrigger>
              );
            })}
          </TabsList>

          <TabsContent value="financial"><FinancialSheet /></TabsContent>
          <TabsContent value="donations"><DonationsSheet pools={poolsForFilter} /></TabsContent>
          <TabsContent value="debt-queue"><DebtQueueSheet /></TabsContent>
          <TabsContent value="payouts"><PayoutsSheet /></TabsContent>
          <TabsContent value="users"><UsersSheet /></TabsContent>
          <TabsContent value="pools"><PoolsSheet /></TabsContent>
          <TabsContent value="fraud"><FraudSheet /></TabsContent>
          <TabsContent value="jobs"><JobsSheet /></TabsContent>
        </Tabs>
      </div>
      <QuoteCarousel />

    </div>
  );
}
